// Screen space clustering for map markers.
//
// WHY SCREEN SPACE, not a geographic radius: a fixed radius in metres is
// correct at exactly one zoom level. At z18 a 200 m radius merges markers
// that are visibly far apart, at z4 it merges nothing while ten avatars sit
// on the same three pixels. Clustering on projected pixel distance is
// zoom-correct by construction: the same threshold means the same visual
// crowding at every zoom, and zooming in always breaks a cluster apart
// because the projected distance between two fixed coordinates grows.
//
// The identical coordinate case is the one that must not degrade: ten
// travellers at the same lat and lng project to distance 0, land in one
// cluster, and render as ONE marker with an exact count, never ten markers
// stacked into an unreadable blob.

export interface ClusterPoint<T> {
  /** Stable identity, used to key the DOM marker. */
  id: string;
  lng: number;
  lat: number;
  item: T;
}

export interface ScreenPoint {
  x: number;
  y: number;
}

export interface Cluster<T> {
  /**
   * Stable id derived from the leading member, so a marker survives a
   * recompute on pan and zoom instead of being torn down and rebuilt.
   */
  id: string;
  /**
   * Anchor coordinate. The leading member's exact coordinate, never an
   * average: for the identical coordinate case an average would drift the
   * marker off the shared point for no benefit.
   */
  lng: number;
  lat: number;
  members: Array<ClusterPoint<T>>;
}

/** Default crowding threshold in CSS pixels for avatar sized markers. */
export const AVATAR_CLUSTER_PX = 40;
/** Default crowding threshold for the smaller moment pins. */
export const MOMENT_CLUSTER_PX = 30;

/**
 * Greedy single pass grouping in projected pixel space.
 *
 * Input order is meaningful: the first member of a cluster is the leader
 * (rendered in front, and its coordinate anchors the marker), so callers
 * sort by recency before calling. Grid bucketing keeps this O(n) for the
 * marker counts a map ever shows, instead of O(n^2) over every pair.
 */
export function clusterByScreen<T>(
  points: Array<ClusterPoint<T>>,
  project: (point: ClusterPoint<T>) => ScreenPoint,
  thresholdPx: number,
): Array<Cluster<T>> {
  const threshold = Math.max(0, thresholdPx);
  const cell = Math.max(1, threshold);
  const buckets = new Map<string, number[]>();
  const clusters: Array<Cluster<T>> = [];
  const anchors: ScreenPoint[] = [];

  const keyOf = (x: number, y: number) => `${Math.floor(x / cell)}:${Math.floor(y / cell)}`;

  for (const point of points) {
    if (!Number.isFinite(point.lat) || !Number.isFinite(point.lng)) continue;
    const screen = project(point);
    if (!Number.isFinite(screen.x) || !Number.isFinite(screen.y)) continue;

    let joined = -1;
    let bestDistance = Number.POSITIVE_INFINITY;
    const cx = Math.floor(screen.x / cell);
    const cy = Math.floor(screen.y / cell);
    // A cluster anchor within `threshold` can only live in this cell or one
    // of the eight around it, because the cell size is the threshold.
    for (let dx = -1; dx <= 1 && threshold > 0; dx += 1) {
      for (let dy = -1; dy <= 1; dy += 1) {
        const candidates = buckets.get(`${cx + dx}:${cy + dy}`);
        if (!candidates) continue;
        for (const index of candidates) {
          const anchor = anchors[index]!;
          const distance = Math.hypot(anchor.x - screen.x, anchor.y - screen.y);
          if (distance <= threshold && distance < bestDistance) {
            bestDistance = distance;
            joined = index;
          }
        }
      }
    }

    if (joined >= 0) {
      clusters[joined]!.members.push(point);
      continue;
    }

    const index = clusters.length;
    clusters.push({ id: point.id, lng: point.lng, lat: point.lat, members: [point] });
    anchors.push(screen);
    const key = keyOf(screen.x, screen.y);
    const bucket = buckets.get(key);
    if (bucket) bucket.push(index);
    else buckets.set(key, [index]);
  }

  return clusters;
}

/**
 * How a cluster renders. Two or three travellers fan out as an overlapping
 * stack so you can still see who is there; four or more show the leading
 * faces plus an exact overflow count.
 */
export const STACK_MAX = 3;
/**
 * With an overflow chip present, three faces plus the chip is the layout
 * Brian approved: ten travellers on one point read as three characters and a
 * "+7", which still adds up to ten.
 */
export const FACES_WITH_OVERFLOW = 3;

export interface ClusterDisplay {
  /** How many member avatars to draw. */
  visible: number;
  /** Exact remainder, 0 when every member is drawn. */
  overflow: number;
  /** True for the fanned stack of 2 to 3, false for a single pin. */
  fanned: boolean;
}

export function clusterDisplay(count: number): ClusterDisplay {
  if (count <= 1) return { visible: Math.max(0, count), overflow: 0, fanned: false };
  if (count <= STACK_MAX) return { visible: count, overflow: 0, fanned: true };
  return {
    visible: FACES_WITH_OVERFLOW,
    overflow: count - FACES_WITH_OVERFLOW,
    fanned: true,
  };
}
